import { Link, useLocation, useParams } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useBreadcrumbCache } from "@/lib/breadcrumbCache";

// Matches a pathname segment path against a set of known patterns and returns
// an ordered list of (path, label) crumbs. We do this by hand rather than via
// `useMatches()` because the app is mounted under a non-data <BrowserRouter>
// and `useMatches()` only works with createBrowserRouter/DataRouter.
interface Crumb {
  to: string;
  label: string;
}

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const params = useParams();
  const cache = useBreadcrumbCache();
  const crumbs = resolveCrumbs(pathname, params, cache.get);

  if (crumbs.length <= 1) return null;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <BreadcrumbItem key={c.to + i}>
              {last ? (
                <BreadcrumbPage>{c.label}</BreadcrumbPage>
              ) : (
                <>
                  <BreadcrumbLink asChild>
                    <Link to={c.to}>{c.label}</Link>
                  </BreadcrumbLink>
                  <BreadcrumbSeparator />
                </>
              )}
            </BreadcrumbItem>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

type CacheLookup = (kind: string, id: string) => string | undefined;

function resolveCrumbs(
  pathname: string,
  params: Record<string, string | undefined>,
  lookup: CacheLookup,
): Crumb[] {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return [];

  switch (segments[0]) {
    case "dashboard":
      return [{ to: "/dashboard", label: "Assessments" }];
    case "progress":
      return [
        { to: "/dashboard", label: "Assessments" },
        { to: "/progress", label: "My progress" },
      ];
    case "stats":
      return [
        { to: "/dashboard", label: "Assessments" },
        { to: "/stats", label: "Stats" },
      ];
    case "authoring": {
      const crumbs: Crumb[] = [{ to: "/authoring", label: "Authoring" }];
      if (params.assessmentId) {
        const label =
          lookup("assessment", params.assessmentId) ?? "Edit assessment";
        crumbs.push({ to: `/authoring/${params.assessmentId}`, label });
      }
      return crumbs;
    }
    case "marking": {
      const crumbs: Crumb[] = [{ to: "/marking", label: "Marking" }];
      if (params.attemptId) {
        if (pathname.endsWith("/moderate")) {
          crumbs.push({ to: pathname, label: "Moderate" });
        } else {
          const label =
            lookup("attempt", params.attemptId) ?? "Mark attempt";
          crumbs.push({ to: `/marking/${params.attemptId}`, label });
        }
      }
      return crumbs;
    }
    case "attempts":
      if (params.attemptId && pathname.endsWith("/result")) {
        return [
          { to: "/dashboard", label: "Assessments" },
          { to: pathname, label: "Result" },
        ];
      }
      return [];
    case "platform": {
      const crumbs: Crumb[] = [{ to: "/platform", label: "Platform" }];
      if (segments[1] === "tenants" && params.tenantId) {
        const slug = lookup("tenant", params.tenantId);
        crumbs.push({
          to: `/platform/tenants/${params.tenantId}`,
          label: slug ?? "Tenant",
        });
      } else if (segments[1] === "metrics") {
        crumbs.push({ to: "/platform/metrics", label: "Metrics" });
      } else if (segments[1] === "programmes") {
        crumbs.push({ to: "/platform/programmes", label: "Programmes" });
      } else if (segments[1] === "audit") {
        crumbs.push({ to: "/platform/audit", label: "Audit log" });
      } else if (segments[1] === "deletion-requests") {
        crumbs.push({
          to: "/platform/deletion-requests",
          label: "Deletion requests",
        });
      } else if (segments[1] === "billing") {
        crumbs.push({ to: "/platform/billing", label: "Billing" });
      } else if (segments[1] === "errors") {
        crumbs.push({ to: "/platform/errors", label: "Errors" });
      } else if (segments[1] === "diagnostics") {
        crumbs.push({ to: "/platform/diagnostics", label: "Diagnostics" });
      } else if (segments[1] === "sets") {
        if (segments[2] === "new") {
          crumbs.push({ to: "/platform/sets/new", label: "New set" });
        } else if (params.id) {
          crumbs.push({ to: `/platform/sets/${params.id}`, label: "Edit set" });
        }
      } else if (segments[1] === "questions") {
        crumbs.push({ to: "/platform/questions", label: "Question bank" });
        if (segments[2] === "review-queue") {
          crumbs.push({
            to: "/platform/questions/review-queue",
            label: "Review queue",
          });
        } else if (segments[2] === "moderation") {
          crumbs.push({
            to: "/platform/questions/moderation",
            label: "Moderation",
          });
        } else if (params.id && segments[3] === "edit") {
          crumbs.push({
            to: `/platform/questions/${params.id}/edit`,
            label: lookup("platformQuestion", params.id) ?? "Edit question",
          });
        } else if (params.id) {
          crumbs.push({
            to: `/platform/questions/${params.id}`,
            label: lookup("platformQuestion", params.id) ?? "Question",
          });
        }
      }
      return crumbs;
    }
  }
  return [];
}
